import { hashFnv1a, rngForSeed, rr, selectCover } from "./cover-art"
import { DISPLAY, MONO } from "./type-voices"

/**
 * The destination's catalog number — a stable print mark derived from its
 * natural key, shared by the label art and the card meta line. (Never derive
 * this from impression_id: that's a fresh row every render.)
 */
export function catalogNumber(seed: string): string {
  return `no. ${100 + (hashFnv1a(seed) % 900)}`
}

/** One word → its first two characters; two+ words → their initials. */
function initialsFor(title: string): string {
  const words = title.split(/\s+/).filter(Boolean)
  if (words.length === 0) return "·"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

/**
 * OutroLabelArt — the letterpress "label" a destination prints when it has no
 * card image: a quiet framed plate on the colourway's paper tint, the
 * destination's initials set in the display slab, and its catalog number in
 * mono. Deterministic from the destination key (cover-art's seeded machinery),
 * so the same service always prints the same label. Deliberately quieter than
 * the jackets' six motifs — destinations borrow the palette, not the patterns.
 */
export function OutroLabelArt({
  seed,
  title,
  className,
}: {
  seed: string
  title: string
  className?: string
}) {
  const cover = selectCover(seed)
  const rng = rngForSeed(seed, cover.key)

  // One seeded accent per label: a short letterpress rule under the initials,
  // its width and offset the label's own — in the jacket field colour, the
  // only place `base` appears.
  const ruleWidth = rr(rng, 28, 64)
  const ruleX = 160 - ruleWidth / 2 + rr(rng, -10, 10)
  const dotCount = rng() > 0.5 ? Math.floor(rr(rng, 2, 4)) : 0

  return (
    <svg
      viewBox="0 0 320 180"
      preserveAspectRatio="xMidYMid slice"
      className={className ?? "h-full w-full"}
      aria-hidden="true"
      role="presentation"
    >
      <rect width="320" height="180" fill={cover.glow} />
      <rect
        x="8"
        y="8"
        width="304"
        height="164"
        fill="none"
        stroke={cover.ink}
        strokeOpacity="0.55"
        strokeWidth="1"
      />
      <rect
        x="12"
        y="12"
        width="296"
        height="156"
        fill="none"
        stroke={cover.ink}
        strokeOpacity="0.55"
        strokeWidth="1"
      />
      <text
        x="160"
        y="84"
        textAnchor="middle"
        dominantBaseline="central"
        fill={cover.ink}
        style={{
          fontFamily: DISPLAY,
          fontWeight: 700,
          fontSize: 64,
          letterSpacing: "0.02em",
        }}
      >
        {initialsFor(title)}
      </text>
      <rect x={ruleX} y="126" width={ruleWidth} height="2" fill={cover.base} />
      {Array.from({ length: dotCount }, (_, i) => (
        <circle
          key={i}
          cx={ruleX + ruleWidth + 8 + i * 8}
          cy="127"
          r="1.5"
          fill={cover.base}
        />
      ))}
      <text
        x="300"
        y="160"
        textAnchor="end"
        fill={cover.ink}
        fillOpacity="0.55"
        style={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: "0.08em",
        }}
      >
        {catalogNumber(seed)}
      </text>
    </svg>
  )
}
